import apiClient from "../lib/api-client";

export type SalesReturnStatus = "DRAFT" | "APPROVED" | "REFUNDED" | "CANCELLED";

export type ReturnReason =
  | "DAMAGED"
  | "DEFECTIVE"
  | "EXPIRED"
  | "WRONG_ITEM"
  | "QUALITY_ISSUE"
  | "OVERSTOCK"
  | "RECALL"
  | "OTHER";

export interface SalesReturnItem {
  id: string;
  product: { id: string; name: string; sku?: string };
  batch: { id: string; batchNumber: string };
  qtyReturned: number;
  rate: number;
  discount: number;
  gstPercent: number;
  gstAmount: number;
  totalAmount: number;
  reason?: string;
  condition?:
    | "SEALED_SALEABLE"
    | "OPENED"
    | "DAMAGED"
    | "EXPIRED"
    | "RECALLED"
    | "COLD_CHAIN_UNVERIFIED";
  disposition?:
    | "AVAILABLE"
    | "QUARANTINE"
    | "DAMAGED"
    | "EXPIRED"
    | "RECALLED"
    | "SUPPLIER_CLAIM";
  stockDisposition?:
    | "AVAILABLE"
    | "QUARANTINE"
    | "DAMAGED"
    | "EXPIRED"
    | "RECALLED"
    | "SUPPLIER_CLAIM";
}

export interface SalesReturn {
  id: string;
  returnNumber: string;
  returnDate: string;
  status: SalesReturnStatus;
  returnReason: ReturnReason;
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  notes?: string | null;
  invoice?: { id: string; invoiceNumber: string; invoiceDate?: string };
  customer?: { id: string; name: string; phone?: string } | null;
  items: SalesReturnItem[];
}

interface InventoryOperation {
  id: string;
  status: string;
  version: number;
}

function mutationKey(storageKey: string) {
  const existing = sessionStorage.getItem(storageKey);
  if (existing) return existing;
  const key =
    globalThis.crypto?.randomUUID?.() ??
    `inventory-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  sessionStorage.setItem(storageKey, key);
  return key;
}

async function mutateInventoryOperation(
  path: string,
  body: Record<string, unknown>,
  storageKey: string,
) {
  const response = await apiClient.post<{ operation: InventoryOperation }>(
    path,
    body,
    { headers: { "Idempotency-Key": mutationKey(storageKey) } },
  );
  sessionStorage.removeItem(storageKey);
  return response.data.operation;
}

export const salesReturnService = {
  async getSalesReturns(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: SalesReturnStatus;
    storeId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    data: SalesReturn[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const response = await apiClient.get("/sales-returns", { params });
    return response.data;
  },

  async getSalesReturn(id: string): Promise<SalesReturn> {
    const response = await apiClient.get(`/sales-returns/${id}`);
    return response.data;
  },

  async releaseQuarantinedItem(
    salesReturn: Pick<SalesReturn, "id" | "returnNumber">,
    item: Pick<SalesReturnItem, "id" | "product" | "batch" | "qtyReturned">,
  ): Promise<void> {
    const operationScope = `${salesReturn.id}:${item.id}`;
    const reason = `Return ${salesReturn.returnNumber} inspected as sealed and saleable`;
    let operation = await mutateInventoryOperation(
      "/inventory-operations",
      {
        operationType: "QUARANTINE",
        productId: item.product.id,
        lotId: item.batch.id,
        quantity: Number(item.qtyReturned).toFixed(6),
        sourceType: "SALE_RETURN_STOCK_RELEASE",
        sourceId: salesReturn.id,
        sourceLineId: item.id,
        reason,
        facts: {
          returnNumber: salesReturn.returnNumber,
          inspectionResult: "SEALED_SALEABLE",
        },
      },
      `medicorepay:return-stock-release:${operationScope}:open`,
    );

    if (operation.status === "OPEN") {
      operation = await mutateInventoryOperation(
        `/inventory-operations/${encodeURIComponent(operation.id)}/transitions`,
        {
          nextStatus: "UNDER_REVIEW",
          expectedVersion: operation.version,
          reason: "Physical condition inspection started",
        },
        `medicorepay:return-stock-release:${operationScope}:review:${operation.version}`,
      );
    }
    if (
      operation.status === "UNDER_REVIEW" ||
      operation.status === "PARTIALLY_RELEASED"
    ) {
      operation = await mutateInventoryOperation(
        `/inventory-operations/${encodeURIComponent(operation.id)}/transitions`,
        {
          nextStatus: "RELEASED",
          expectedVersion: operation.version,
          reason,
          evidence: {
            inspectionResult: "SEALED_SALEABLE",
            confirmedAt: new Date().toISOString(),
          },
        },
        `medicorepay:return-stock-release:${operationScope}:release:${operation.version}`,
      );
    }
    if (operation.status !== "RELEASED") {
      throw new Error(
        `Return stock cannot be released from ${operation.status.toLowerCase()}`,
      );
    }
  },
};
